import { SkipNext } from '@mui/icons-material';
import {
    Box,
    Button,
    Stack,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { Check, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { RoiId } from '~/domain/changes/ids';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { useConnectorIdentity } from '~/hooks/auth/useConnectorIdentity';
import { useReviewer } from '~/hooks/domain/useReviewer';
import { useRoiCommands } from '~/hooks/domain/useRoiCommands';
import useRoi from '~/hooks/structure-set/useRoi';
import {
    openedStructureSetApprovalConfirmationDialogState,
    selectedROIState,
} from '~/state/atoms/structureSet/roiAtoms';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { getNextRoiNumber } from '~/containers/viewer/explorer-sidebar/WorkflowSidebar/ContouringStep/StructureSet/roiSort';
import AppConfig from '~/utils/appconfig';

export default function ReviewControls({
    roi,
    roiId,
}: {
    roi: RoiReadModel;
    /** Branded RoiId from the projection layer. When present, approval
     * transitions are dispatched through useRoiCommands (new path). Falls
     * back to the legacy useRoi domain-event path when absent. */
    roiId?: RoiId;
}) {
    const { setRoiApprovalStatus } = useRoi(roi);
    const { approve, reject, unapprove } = useRoiCommands();
    const reviewer = useReviewer();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const setSelectedROI = useSetRecoilState<number>(selectedROIState);
    const setOpenedApprovalDialog = useSetRecoilState(
        openedStructureSetApprovalConfirmationDialogState
    );
    const { palette } = useTheme();

    const setApprovalStatus = useCallback(
        (status: DICOMApprovalStatus) => {
            if (roiId !== undefined) {
                switch (status) {
                    case DICOMApprovalStatus.APPROVED:
                        approve(roiId, reviewer);
                        break;
                    case DICOMApprovalStatus.REJECTED:
                        reject(roiId, reviewer);
                        break;
                    case DICOMApprovalStatus.UNAPPROVED:
                        unapprove(roiId);
                        break;
                }
            } else {
                setRoiApprovalStatus(status);
            }
        },
        [roiId, reviewer, approve, reject, unapprove, setRoiApprovalStatus]
    );

    const handleReview = (status?: DICOMApprovalStatus) => {
        if (!currentStructureSet?.roiArray) {
            return;
        }

        const nextRoiNumber = getNextRoiNumber(
            currentStructureSet.roiArray,
            roi.roiNumber
        );

        if (nextRoiNumber !== undefined) {
            flushSync(() => setSelectedROI(nextRoiNumber));
        }

        // A click that keeps the status moves on without a write, so the
        // recorded reviewer of an unchanged review stays shown.
        if (status && status !== roi.approvalStatus) {
            setApprovalStatus(status);
        }
    };

    const reviewedStructuresAmount = currentStructureSet?.roiArray.filter(
        (struct) => struct.approvalStatus !== DICOMApprovalStatus.UNAPPROVED
    ).length;

    // Per-ROI review is only available while the set is UNAPPROVED.
    // DICOM carries a single set-level approval status, so once the set
    // is signed off (APPROVED/REJECTED) per-structure review is locked;
    // the reviewer must unapprove the whole set to re-review. RoiService
    // enforces the same rule (ReviewLockedError) as a fail-safe.
    const isReviewLocked =
        currentStructureSet != null &&
        currentStructureSet.approvalStatus !== DICOMApprovalStatus.UNAPPROVED;

    // The connector records a structure's review under the signed-in account
    // and refuses one from anyone it cannot identify, so where there is a
    // connector a decision needs a sign-in.
    const identity = useConnectorIdentity();
    const needsSignIn =
        AppConfig.isConnectorMode &&
        (!identity.signInEnabled || !identity.account);
    const isDecisionLocked = isReviewLocked || needsSignIn;

    // Once every structure has been reviewed (none left UNAPPROVED) on a
    // not-yet-signed-off set, offer to sign off the whole set. Set status
    // only ever moves through the explicit confirmation dialog, so this is
    // a discoverable shortcut into it — not an automatic roll-up.
    const roiCount = currentStructureSet?.roiArray.length ?? 0;
    const allStructuresReviewed =
        currentStructureSet != null &&
        roiCount > 0 &&
        reviewedStructuresAmount === roiCount;

    const [approvalPromptDismissed, setApprovalPromptDismissed] =
        useState(false);
    // A dismissal only suppresses the current completion; if the review
    // state regresses and completes again, re-offer the prompt.
    useEffect(() => {
        if (!allStructuresReviewed) {
            setApprovalPromptDismissed(false);
        }
    }, [allStructuresReviewed]);

    const showApprovalPrompt =
        !isReviewLocked && allStructuresReviewed && !approvalPromptDismissed;

    const openSetApprovalDialog = (status: DICOMApprovalStatus) => {
        if (currentStructureSet) {
            setOpenedApprovalDialog({
                sopUid: currentStructureSet.dicomUid,
                newApprovalStatus: status,
            });
        }
    };

    return (
        <Stack spacing={0.5}>
            <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" align="center" fontSize={12}>
                    Structures reviewed
                </Typography>
                <Typography variant="body2" align="center" fontSize={12}>
                    {reviewedStructuresAmount} /{' '}
                    {currentStructureSet?.roiArray.length}{' '}
                </Typography>
            </Box>
            <Stack spacing={1} direction="row" justifyContent="space-evenly">
                <Tooltip title="Approve" placement="top">
                    {/* span wrapper: Tooltip needs a non-disabled child to
                        listen for events when the button is locked */}
                    <span style={{ flexGrow: 1, display: 'flex' }}>
                        <Button
                            variant="outlined"
                            color="success"
                            fullWidth
                            size="small"
                            disabled={isDecisionLocked}
                            onClick={() =>
                                handleReview(DICOMApprovalStatus.APPROVED)
                            }
                            sx={{
                                ...(roi.approvalStatus ===
                                DICOMApprovalStatus.APPROVED
                                    ? {
                                          backgroundColor: 'success.main',
                                          color: 'success.contrastText',
                                      }
                                    : {}),
                            }}
                            data-testid="approve-button"
                        >
                            <Check
                                color={
                                    roi.approvalStatus ===
                                    DICOMApprovalStatus.APPROVED
                                        ? palette.success.contrastText
                                        : palette.success.main
                                }
                            />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Reject" placement="top">
                    <span style={{ flexGrow: 1, display: 'flex' }}>
                        <Button
                            variant="outlined"
                            color="error"
                            fullWidth
                            size="small"
                            disabled={isDecisionLocked}
                            onClick={() =>
                                handleReview(DICOMApprovalStatus.REJECTED)
                            }
                            sx={{
                                ...(roi.approvalStatus ===
                                DICOMApprovalStatus.REJECTED
                                    ? {
                                          backgroundColor: 'error.main',
                                          color: 'error.contrastText',
                                      }
                                    : {}),
                            }}
                            data-testid="reject-button"
                        >
                            <X
                                color={
                                    roi.approvalStatus ===
                                    DICOMApprovalStatus.REJECTED
                                        ? palette.error.contrastText
                                        : palette.error.main
                                }
                            />
                        </Button>
                    </span>
                </Tooltip>
                <Tooltip title="Next" placement="top">
                    <span style={{ flexGrow: 1, display: 'flex' }}>
                        <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            disabled={isReviewLocked}
                            onClick={() => handleReview()}
                            sx={{
                                fontSize: 12,
                            }}
                            data-testid="next-button"
                        >
                            <SkipNext />
                        </Button>
                    </span>
                </Tooltip>
            </Stack>
            {needsSignIn && !isReviewLocked && (
                <Typography
                    variant="body2"
                    fontSize={11}
                    color="text.secondary"
                    sx={{ pt: 0.5 }}
                    data-testid="review-sign-in-hint"
                >
                    <FormattedMessage id="workflow.review.signInToReview" />
                </Typography>
            )}
            {isReviewLocked && (
                <Typography
                    variant="body2"
                    fontSize={11}
                    color="text.secondary"
                    sx={{ pt: 0.5 }}
                    data-testid="review-locked-hint"
                >
                    The structure set is {currentStructureSet?.approvalStatus}.
                    Unapprove it to re-review its structures.
                </Typography>
            )}
            {showApprovalPrompt && (
                <Box data-testid="approval-prompt" sx={{ pt: 0.5 }}>
                    <Typography
                        variant="body2"
                        fontSize={11}
                        color="text.secondary"
                    >
                        All structures reviewed. Set the structure set as:
                    </Typography>
                    <Stack
                        spacing={1}
                        direction="row"
                        sx={{ pt: 0.5 }}
                        justifyContent="space-evenly"
                    >
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={() =>
                                openSetApprovalDialog(
                                    DICOMApprovalStatus.APPROVED
                                )
                            }
                            data-testid="approve-set-button"
                        >
                            Approve
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() =>
                                openSetApprovalDialog(
                                    DICOMApprovalStatus.REJECTED
                                )
                            }
                            data-testid="reject-set-button"
                        >
                            Reject
                        </Button>
                        <Button
                            size="small"
                            variant="text"
                            onClick={() => setApprovalPromptDismissed(true)}
                            data-testid="dismiss-prompt-button"
                        >
                            Dismiss
                        </Button>
                    </Stack>
                </Box>
            )}
        </Stack>
    );
}
